import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getNotificationPreferences, createNotificationWithEmail, getInvestments } from '../services/supabaseService';
import { 
  useTransactionsRealtime,
  useBudgetCategoriesRealtime,
  useFinancialGoalsRealtime
} from '../hooks/useRealtimeSubscription';

const NotificationMonitor = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [lastCheck, setLastCheck] = useState(Date.now());
  const lastBillCheckRef = useRef(null);
  const lastInvestmentCheckRef = useRef(null);
  const notifiedBillsRef = useRef(new Set());
  const notifiedGoalsRef = useRef(new Set());

  // Load preferences on mount
  useEffect(() => {
    if (user) {
      loadPreferences();
    }
  }, [user]);

  const loadPreferences = async () => {
    try {
      const prefs = await getNotificationPreferences(user?.id);
      setPreferences(prefs);
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
    }
  };

  // Realtime monitoring for transactions
  useTransactionsRealtime(user?.id, {
    onInsert: async (newTransaction) => {
      if (!preferences?.budgetAlerts) return;

      // Check spending threshold
      if (newTransaction?.amount >= (preferences?.spendingThreshold || 500)) {
        try {
          await createNotificationWithEmail(
            user?.id,
            {
              notificationType: 'spending_threshold',
              title: 'Large Transaction Alert',
              message: `A transaction of $${parseFloat(newTransaction?.amount)?.toFixed(2)} was recorded: ${newTransaction?.description}`,
              category: 'spending',
              priority: 'high',
              metadata: {
                transactionId: newTransaction?.id,
                amount: newTransaction?.amount,
                description: newTransaction?.description,
                date: newTransaction?.date
              }
            },
            preferences?.emailEnabled && preferences?.emailFrequency === 'immediate'
          );
        } catch (err) {
          console.error('[NotificationMonitor] Failed to create spending threshold notification:', err);
        }
      }
    }
  });

  // Realtime monitoring for budget categories - budget overage alerts
  useBudgetCategoriesRealtime(user?.id, {
    onUpdate: async (updatedCategory) => {
      if (!preferences?.budgetAlerts) return;

      const budgetAmount = parseFloat(updatedCategory?.budget_amount || 0);
      const spentAmount = parseFloat(updatedCategory?.spent_amount || 0);
      if (budgetAmount <= 0) return;

      const percentUsed = (spentAmount / budgetAmount) * 100;

      // Alert when exceeding budget threshold
      if (spentAmount > budgetAmount) {
        const overage = spentAmount - budgetAmount;
        try {
          await createNotificationWithEmail(
            user?.id,
            {
              notificationType: 'budget_limit',
              title: `Budget Exceeded: ${updatedCategory?.name}`,
              message: `You have exceeded your ${updatedCategory?.name} budget by $${overage?.toFixed(2)}. Budget: $${budgetAmount?.toFixed(2)}, Spent: $${spentAmount?.toFixed(2)}`,
              category: 'spending',
              priority: 'critical',
              metadata: {
                categoryId: updatedCategory?.id,
                categoryName: updatedCategory?.name,
                overage: overage,
                budgetAmount: budgetAmount,
                spentAmount: spentAmount
              }
            },
            preferences?.emailEnabled && preferences?.emailFrequency === 'immediate'
          );
        } catch (err) {
          console.error('[NotificationMonitor] Failed to create budget overage notification:', err);
        }
      }
      // Warning when approaching limit (90%)
      else if (percentUsed >= 90 && percentUsed < 100) {
        try {
          await createNotificationWithEmail(
            user?.id,
            {
              notificationType: 'budget_limit',
              title: `Budget Warning: ${updatedCategory?.name}`,
              message: `You have used ${percentUsed?.toFixed(0)}% of your ${updatedCategory?.name} budget ($${spentAmount?.toFixed(2)} of $${budgetAmount?.toFixed(2)})`,
              category: 'spending',
              priority: 'medium',
              metadata: {
                categoryId: updatedCategory?.id,
                categoryName: updatedCategory?.name,
                percentUsed: percentUsed,
                budgetAmount: budgetAmount,
                spentAmount: spentAmount
              }
            },
            false // Don't send email for warnings
          );
        } catch (err) {
          console.error('[NotificationMonitor] Failed to create budget warning notification:', err);
        }
      }
    }
  });

  // Realtime monitoring for financial goals - milestone notifications
  useFinancialGoalsRealtime(user?.id, {
    onUpdate: async (updatedGoal, oldGoal) => {
      if (!preferences?.goalMilestones) return;

      const targetAmount = parseFloat(updatedGoal?.target_amount || 0);
      const currentAmount = parseFloat(updatedGoal?.current_amount || 0);
      const oldCurrentAmount = parseFloat(oldGoal?.current_amount || 0);

      if (targetAmount <= 0) return;

      const oldProgress = (oldCurrentAmount / targetAmount) * 100;
      const newProgress = (currentAmount / targetAmount) * 100;

      // Check for milestone achievements (25%, 50%, 75%, 100%)
      const milestones = [25, 50, 75, 100];
      const achievedMilestone = milestones?.find(
        m => oldProgress < m && newProgress >= m
      );

      if (achievedMilestone) {
        // Prevent duplicate notifications for same goal+milestone
        const notifKey = `${updatedGoal?.id}-${achievedMilestone}`;
        if (notifiedGoalsRef?.current?.has(notifKey)) return;
        notifiedGoalsRef?.current?.add(notifKey);

        const isComplete = achievedMilestone === 100;
        const congratsMsg = isComplete
          ? `🎉 Congratulations! You've fully achieved your goal: "${updatedGoal?.title}"! Target: $${targetAmount?.toFixed(2)}`
          : `You've reached ${achievedMilestone}% of your goal: "${updatedGoal?.title}" ($${currentAmount?.toFixed(2)} of $${targetAmount?.toFixed(2)})`;

        try {
          await createNotificationWithEmail(
            user?.id,
            {
              notificationType: 'goal_progress',
              title: isComplete ? `🎉 Goal Achieved: ${updatedGoal?.title}!` : `Goal Milestone: ${achievedMilestone}% Complete!`,
              message: congratsMsg,
              category: 'goal',
              priority: isComplete ? 'high' : 'medium',
              metadata: {
                goalId: updatedGoal?.id,
                goalTitle: updatedGoal?.title,
                targetAmount: targetAmount,
                currentAmount: currentAmount,
                progress: newProgress,
                milestone: achievedMilestone
              }
            },
            preferences?.emailEnabled &&
            (preferences?.emailFrequency === 'immediate' || isComplete)
          );
        } catch (err) {
          console.error('[NotificationMonitor] Failed to create goal milestone notification:', err);
        }
      }
    }
  });

  // Periodic check for bill reminders and investment alerts
  useEffect(() => {
    if (!user || !preferences) return;

    const checkBillReminders = async () => {
      if (!preferences?.billReminders) return;

      try {
        const { data: bills, error } = await supabase
          ?.from('recurring_bills')
          ?.select('*')
          ?.eq('user_id', user?.id)
          ?.eq('is_active', true);

        if (error || !bills?.length) return;

        const today = new Date();
        today?.setHours(0, 0, 0, 0);

        for (const bill of bills) {
          const dueDateStr = bill?.next_due_date || bill?.due_date;
          if (!dueDateStr) continue;

          const dueDate = new Date(dueDateStr);
          dueDate?.setHours(0, 0, 0, 0);
          const daysUntilDue = Math.round((dueDate - today) / (1000 * 60 * 60 * 24));

          // Send reminders at 3 days and 1 day before due
          if (daysUntilDue === 3 || daysUntilDue === 1) {
            const billKey = `bill-${bill?.id}-${daysUntilDue}d-${today?.toDateString()}`;
            if (notifiedBillsRef?.current?.has(billKey)) continue;
            notifiedBillsRef?.current?.add(billKey);

            const billName = bill?.bill_name || 'Bill';
            const amount = parseFloat(bill?.amount || 0);

            try {
              await createNotificationWithEmail(
                user?.id,
                {
                  notificationType: 'bill_reminder',
                  title: `Bill Due in ${daysUntilDue} Day${daysUntilDue > 1 ? 's' : ''}: ${billName}`,
                  message: `Your ${billName} payment of $${amount?.toFixed(2)} is due in ${daysUntilDue} day${daysUntilDue > 1 ? 's' : ''} on ${dueDate?.toLocaleDateString('en-CA')}.`,
                  category: 'bill',
                  priority: daysUntilDue === 1 ? 'high' : 'medium',
                  metadata: {
                    billId: bill?.id,
                    billName: billName,
                    amount: amount,
                    dueDate: dueDate?.toISOString(),
                    daysUntilDue: daysUntilDue
                  }
                },
                preferences?.emailEnabled && preferences?.emailFrequency === 'immediate'
              );
            } catch (err) {
              console.error(`[NotificationMonitor] Failed to create bill reminder for ${billName}:`, err);
            }
          }
        }

        lastBillCheckRef.current = Date.now();
        setLastCheck(Date.now());
      } catch (error) {
        console.error('[NotificationMonitor] Bill reminder check error:', error);
      }
    };

    const checkInvestmentAlerts = async () => {
      if (!preferences?.investmentAlerts) return;

      try {
        const investments = await getInvestments(user?.id);
        if (!investments?.length) return;

        const totalValue = investments?.reduce((sum, inv) => {
          return sum + (parseFloat(inv?.quantity || 0) * parseFloat(inv?.currentPrice || inv?.purchasePrice || 0));
        }, 0);

        const totalCost = investments?.reduce((sum, inv) => {
          return sum + (parseFloat(inv?.quantity || 0) * parseFloat(inv?.purchasePrice || 0));
        }, 0);

        if (totalCost <= 0) return;

        const portfolioChangePercent = ((totalValue - totalCost) / totalCost) * 100;

        // Alert on significant portfolio changes (>3% loss or >5% gain)
        if (!lastInvestmentCheckRef?.current || (Date.now() - lastInvestmentCheckRef?.current) > 24 * 60 * 60 * 1000) {
          if (Math.abs(portfolioChangePercent) >= 3) {
            const isGain = portfolioChangePercent > 0;
            try {
              await createNotificationWithEmail(
                user?.id,
                {
                  notificationType: 'investment_alert',
                  title: isGain ? `Portfolio Up ${portfolioChangePercent?.toFixed(1)}%` : `Portfolio Down ${Math.abs(portfolioChangePercent)?.toFixed(1)}%`,
                  message: `Your investment portfolio is ${isGain ? 'up' : 'down'} ${Math.abs(portfolioChangePercent)?.toFixed(1)}% (${isGain ? '+' : ''}$${(totalValue - totalCost)?.toFixed(2)}). Current value: $${totalValue?.toFixed(2)}.`,
                  category: 'investment',
                  priority: portfolioChangePercent < -5 ? 'high' : 'medium',
                  metadata: {
                    totalValue: totalValue,
                    totalCost: totalCost,
                    changePercent: portfolioChangePercent,
                    changeAmount: totalValue - totalCost,
                    investmentCount: investments?.length
                  }
                },
                preferences?.emailEnabled && preferences?.emailFrequency === 'immediate'
              );
            } catch (err) {
              console.error('[NotificationMonitor] Failed to create investment alert:', err);
            }
          }
          lastInvestmentCheckRef.current = Date.now();
        }
      } catch (error) {
        console.error('[NotificationMonitor] Investment alert check error:', error);
      }
    };

    // Run checks immediately on mount
    checkBillReminders();
    checkInvestmentAlerts();

    // Check bill reminders every 6 hours
    const billInterval = setInterval(checkBillReminders, 6 * 60 * 60 * 1000);
    // Check investment alerts every 24 hours
    const investmentInterval = setInterval(checkInvestmentAlerts, 24 * 60 * 60 * 1000);

    return () => {
      clearInterval(billInterval);
      clearInterval(investmentInterval);
    };
  }, [user, preferences]);

  return null; // This component doesn't render anything
};

export default NotificationMonitor;